import { memo, useState, useEffect, useMemo } from 'react';
import {
  Cancel,
  Close,
  Save
} from '@mui/icons-material';
import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { grey } from '@mui/material/colors';
import Loader from '../../components/Loader';
import TabNavigation from '../../components/TabNavigation';
import ChosenOptions from './IndividualComponent/ChosenOptions';
import ExtraInformation from './IndividualComponent/ExtraInformation';
import InitialBudget from './IndividualComponent/InitialBudget';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { getCategoryList, getDefaultValuesForIndividualOptions, getTextPriceList, getUnitsList, getHeaderList, getLimitedGuaranteeTextList, addOrUpdateIndividualOption, getIndividualOption } from '../../apis/buyersGuideApis';
import { individualFieldsActions } from '../../_actions';
import { getFormFields, Tabs, getFieldCalculations } from '../../_constants/individualFields.constant';
import TechInfo from './IndividualComponent/TechInfo';

const AddIndividualOption = ({onOpen, onClose, editableId}) => {
  const theme = useTheme();
  const {t} = useTranslation()
  const [tabValue, setTabValue] = useState(0);  
  const [loading, setLoading] = useState(false);
  const { selected } = useSelector((state) => state.buildings);
  const isMobileOrTab = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch()
  const methods = useForm();
  const { allFields } = useSelector(state => state.individualField.initialFieldsList);

  const fieldCalculations = useMemo(() => getFieldCalculations(), []);

  // Get all dependencies and watch them
  const allDependencies = useMemo(() => {
    const deps = new Set(['salesPriceToBeDetermined', 'calculateBaseCostPrice']);
    fieldCalculations.forEach(calc => {
      calc.dependencies.forEach(dep => deps.add(dep));
    });
    return Array.from(deps);
  }, [fieldCalculations]);

  const watchedValues = useWatch({
    control: methods.control,
    name: allDependencies,
  });

  const [salesPriceToBeDetermined, calculateBaseCostPrice] = watchedValues;

  // Handle all calculations in one effect
  useEffect(() => {
    const values = allDependencies.reduce((acc, fieldName, index) => {
      acc[fieldName] = watchedValues[index];
      return acc;
    }, {});

    // Run field calculations
    fieldCalculations.forEach(calc => {
      const newValue = calc.calculate(values);
      const currentValue = methods.getValues(calc.targetField);
      
      // Convert both to numbers for comparison to avoid string vs number issues
      const newValueNum = parseFloat(newValue);
      const currentValueNum = parseFloat(currentValue);
      
      if (isNaN(newValueNum) && isNaN(currentValueNum)) {
        return; // Both are NaN, no update needed
      }
      
      if (newValueNum !== currentValueNum) {
        methods.setValue(calc.targetField, newValue, { shouldValidate: false, shouldDirty: false });
      }
    });

    // Handle baseCostAmount when calculateBaseCostPrice changes
    if (calculateBaseCostPrice === true) {
      methods.setValue('baseCostAmount', 0, { shouldValidate: false, shouldDirty: false });
    }

    // Clear Final Budget fields when salesPriceToBeDetermined is true
    if (salesPriceToBeDetermined === true) {
      if (tabValue === 2) {
        setTabValue(0);
      }
      
      const finalBudgetFields = [
        'salesPriceInclVAT', 'percentageVAT', 'VATAmount', 'salesAmountExclVAT',
        'surchargeDescription1', 'surchargePercentage1', 'surchargeAmount1',
        'surchargeDescription2', 'surchargePercentage2', 'surchargeAmount2',
        'totalSurchargeAmount', 'customSalesPrice',
        'calculateBaseCostPrice', 'baseCostAmount',
        'costDescription1', 'costPercentage1', 'costAmount1',
        'costDescription2', 'costPercentage2', 'costAmount2',
        'costDescription3', 'costPercentage3', 'costAmount3',
        'costDescription4', 'costPercentage4', 'costAmount4',
        'costDescription5', 'costPercentage5', 'costAmount5',
        'totalCostAmount', 'result'
      ];
      
      finalBudgetFields.forEach(fieldName => {
        methods.setValue(fieldName, undefined, { shouldValidate: false, shouldDirty: false });
      });
    }
  }, [watchedValues, fieldCalculations, allDependencies, methods, salesPriceToBeDetermined, calculateBaseCostPrice, tabValue]);

  // Update baseCostAmount disable state
  useEffect(() => {
    if (allFields.length === 0) return;

    const baseCostField = allFields.find(field => field.keyName === 'baseCostAmount');
    const shouldDisable = calculateBaseCostPrice ?? true;

    if (baseCostField && baseCostField.disable !== shouldDisable) {
      const updatedFields = allFields.map(field => {
        if (field.keyName === 'baseCostAmount') {
          return { ...field, disable: shouldDisable };
        }
        return field;
      });

      dispatch(individualFieldsActions.setIndividualFields(updatedFields));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateBaseCostPrice, allFields.length]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const TAB_INDEX_MAP = {
    [Tabs.CHOSEN_OPTIONS]: 0,
    [Tabs.EXTRA_INFORMATION]: 1,
    [Tabs.FINAL_BUDGET]: 2,
    [Tabs.TECH_INFO]: 3,
  };

  // Fetch data and populate form
  useEffect(() => {
    const fetchAllOptions = async () => {
      setLoading(true);
      try {
        const [defaultValuesRes, headerRes, categoryRes, unitsList, textPriceList, limitedGuaranteeTextList] = await Promise.all([
          editableId ? getIndividualOption(editableId) : getDefaultValuesForIndividualOptions(selected.projectId),
          getHeaderList(selected.projectId),
          getCategoryList(selected.projectId),
          getUnitsList(),
          getTextPriceList(),
          getLimitedGuaranteeTextList(),
        ]);
        const defaultValues = defaultValuesRes?.data || {}; 
        
        const dropdownValues = {
          headerOptions: headerRes?.data?.map(item => ({ value: item.id, label: item.name })) || [],
          categoryOptions: categoryRes?.data?.map(item => ({ value: item.id, label: item.name })) || [],
          unitOptions: unitsList?.data?.map(item => ({ value: item.id, label: item.name })) || [],
          textPriceListOptions: textPriceList?.data?.map(item => ({ value: item.id, label: item.name })) || [],
          limitedGuaranteeTextOptions: limitedGuaranteeTextList?.data?.map(item => ({ value: item.id, label: item.name })) || [],
        };

        const projectValues = {
          project: selected.projectName,
          object: selected.buildingNoIntern,
        };

        const fields = getFormFields(t, { ...defaultValues, ...dropdownValues, ...projectValues })
        dispatch(individualFieldsActions.setIndividualFields(fields));
        
        // Populate form if editing
        if (editableId) {
          methods.reset(defaultValues);
        }
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.projectId, editableId]);

  useEffect(() => {
    return () => {
      dispatch(individualFieldsActions.clearIndividualFields());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabItems = useMemo(() => {
    const items = [
      { title: t('general.general'), path: "#", key: 0 },
      { title: t('repairRequest.extraInformation'), path: "#", key: 1 },
      { title: t('buyersGuide.options.individualOption.finalBudget'), path: "#", key: 2 },
      { title: t('general.techInfo'), path: "#", key: 3 },
    ];
    
    if (salesPriceToBeDetermined) {
      return items.filter(item => item.key !== 2);
    }
    return items;
  }, [salesPriceToBeDetermined, t]);

  const flattenFields = (fields = []) => {
    const result = [];
    fields.forEach(field => {
      if (field.groupItems?.questions) {
        result.push(...field.groupItems.questions);
      } else {
        result.push(field);
      }
    });
    return result;
  };

  const getTabIndexFromErrors = (errors, fields) => {
    const errorKeys = Object.keys(errors);
    
    if (!errorKeys.length) return null;

    const flatFields = flattenFields(fields);
    for (const key of errorKeys) {
      const field = flatFields.find(f => f.keyName === key);

      if (field?.fieldDisplayPosition) {
        const tabKey = field.fieldDisplayPosition.split(".")[0];
        return TAB_INDEX_MAP[tabKey] ?? 0;
      }
    }

    return 0;
  };

  const mapFormToPayload = (formData, selected) => {
    const cleanedData = Object.keys(formData).reduce((acc, key) => {
      const value = formData[key];
      if (value !== "" && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    return {
      ...cleanedData,
      buildingId: selected.buildingId,
      projectId: selected.projectId,
    };
  };

  const handleSave = methods.handleSubmit(
    async (data) => {
      try {
        setLoading(true);
        const payload = mapFormToPayload(data, selected);
        await addOrUpdateIndividualOption(payload);
        onClose();
      } catch (error) {
        console.error("Save failed:", error);
      } finally {
        setLoading(false);
      }
    },
    (errors) => {
      const tabIndex = getTabIndexFromErrors(errors, allFields);
      if (tabIndex !== null) {
        setTabValue(tabIndex);
      }
    }
  );

  const handleSaveClick = (event) => {
    handleSave(event);
  };

  return (
    <Dialog
      open={onOpen}
      onClose={onClose}
      fullScreen={isMobileOrTab}
      PaperProps={{
        sx: {
          maxWidth: 'none',
          width: 1200,
          height: { md: 650 },
        },
      }}
    >
      <AppBar position="relative" sx={{ boxShadow: 'none' }}>
        <Toolbar>
          <Stack direction="row" width="100%" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center">
              <IconButton edge="start" color="inherit" onClick={onClose}>
                <Close />
              </IconButton>
              <span style={{ marginLeft: 8, fontSize: 18, fontWeight: 500 }}>
                {t("buyersGuide.options.individualOption")}
              </span>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button size="small" sx={{ display: { xs: 'none', sm: 'inline-flex' } }} variant="text" color="inherit" onClick={onClose} startIcon={<Cancel />}>
                {t('general.cancel')}
              </Button>
              <IconButton sx={{ display: { sm: 'none' } }} color="inherit" onClick={onClose}>
                <Cancel />
              </IconButton>
              <Button sx={{ display: { xs: 'none', sm: 'inline-flex' } }} size="small" variant="text" color="inherit" onClick={handleSaveClick} startIcon={<Save />}>
                {t('general.save')}
              </Button>
              <IconButton sx={{ display: { sm: 'none' } }} color="inherit" onClick={handleSaveClick}>
                <Save />
              </IconButton>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      <FormProvider {...methods}>
        <TabNavigation activeTab={tabValue} tabs={tabItems} handleTabChange={handleTabChange} />
        <DialogContent sx={{ bgcolor: grey[100], p: 2 }}>
          <Paper sx={{ bgcolor: 'common.white' }} elevation={0}>
            {loading && <Loader backdrop />}

            <div style={{ display: tabValue === 0 ? 'block' : 'none' }}><ChosenOptions /></div>
            <div style={{ display: tabValue === 1 ? 'block' : 'none' }}><ExtraInformation/></div>
            {!salesPriceToBeDetermined && (
              <div style={{ display: tabValue === 2 ? 'block' : 'none' }}><InitialBudget /></div>
            )}
            <div style={{ display: (salesPriceToBeDetermined ? tabValue === 2 : tabValue === 3) ? 'block' : 'none' }}><TechInfo /></div>
          </Paper>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};

export default memo(AddIndividualOption);
